import { Injectable } from '@angular/core';
import { SlotClassArgs } from '@progress/kendo-angular-scheduler';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { IWorkTime } from 'src/app/employee/models/work-time';
import { WorkTimeService } from 'src/app/employee/services/work-time.service';
import { TimeSpan } from '../helpers/time-span';

/** A working period within a single day, expressed in minutes from midnight. */
export interface WorkInterval {
  start: number;
  end: number;
}

/**
 * A worktime record reduced to what the scheduler needs: its validity window
 * and the working intervals per weekday (indexed by JS `Date.getDay()`, 0 = Sunday).
 */
export interface WorkTimeSchedule {
  validFrom: number;
  validTo: number | null;
  intervalsByWeekday: WorkInterval[][];
}

/** Maps the flat per-weekday worktime fields to the JS `getDay()` index. */
const WEEKDAY_PREFIXES: Array<{ prefix: string; jsDay: number }> = [
  { prefix: 'monday', jsDay: 1 },
  { prefix: 'tuesday', jsDay: 2 },
  { prefix: 'wednesday', jsDay: 3 },
  { prefix: 'thursday', jsDay: 4 },
  { prefix: 'friday', jsDay: 5 },
  { prefix: 'saturday', jsDay: 6 },
  { prefix: 'sunday', jsDay: 0 },
];

/**
 * Loads employee worktimes and decides whether a scheduler slot lies outside the
 * working hours of an employee. Shared by the main planner (scheduler-shared) and the
 * absence calendar (absence-scheduler) so both gray out the same slots.
 */
@Injectable({ providedIn: 'root' })
export class WorkTimeScheduleService {
  constructor(private workTimeService: WorkTimeService) {}

  /**
   * Fetches the worktimes of the given employees (one request each) and returns the
   * precomputed schedules keyed by employee id. Failures are swallowed so a missing or
   * forbidden worktime never breaks the scheduler.
   */
  loadSchedules(employeeIds: string[]): Observable<Map<string, WorkTimeSchedule[]>> {
    const uniqueIds = Array.from(new Set((employeeIds ?? []).filter(Boolean)));
    if (!uniqueIds.length) {
      return of(new Map<string, WorkTimeSchedule[]>());
    }

    const requests = uniqueIds.map((employeeId) =>
      this.workTimeService
        .searchWorkTimes({ pageNumber: 1, pageSize: 999999, employeeId })
        .pipe(
          map((response) => ({ employeeId, workTimes: response.data ?? [] })),
          catchError(() => of({ employeeId, workTimes: [] as IWorkTime[] }))
        )
    );

    return forkJoin(requests).pipe(
      map((results) => {
        const schedules = new Map<string, WorkTimeSchedule[]>();
        results.forEach(({ employeeId, workTimes }) =>
          schedules.set(
            employeeId,
            workTimes.map((workTime) => this.buildWorkTimeSchedule(workTime))
          )
        );
        return schedules;
      })
    );
  }

  /**
   * Returns true when the given slot lies outside the working hours described by
   * `schedules`. Slots without worktime data (e.g. non-employee ressources) are never
   * treated as non-working, so they stay untouched.
   */
  isNonWorkingSlot(schedules: WorkTimeSchedule[] | undefined, args: SlotClassArgs): boolean {
    if (args.isAllDay || !args.start) {
      return false;
    }

    if (!schedules?.length) {
      return false;
    }

    const slotTime = args.start.getTime();
    const schedule = schedules.find(
      (candidate) =>
        candidate.validFrom <= slotTime &&
        (candidate.validTo == null || slotTime <= candidate.validTo)
    );
    if (!schedule) {
      return false;
    }

    const intervals = schedule.intervalsByWeekday[args.start.getDay()];
    if (!intervals.length) {
      return true;
    }

    const slotStart = args.start.getHours() * 60 + args.start.getMinutes();
    let slotEnd = args.end
      ? args.end.getHours() * 60 + args.end.getMinutes()
      : slotStart;
    // An end of 00:00 means the slot runs to the end of the day.
    if (slotEnd <= slotStart) {
      slotEnd = 24 * 60;
    }

    const overlapsWorkingTime = intervals.some(
      (interval) => slotStart < interval.end && slotEnd > interval.start
    );
    return !overlapsWorkingTime;
  }

  /** Reduces a worktime record to its validity window and per-weekday working intervals. */
  private buildWorkTimeSchedule(workTime: IWorkTime): WorkTimeSchedule {
    const fields = workTime as unknown as Record<string, string>;
    const intervalsByWeekday: WorkInterval[][] = [[], [], [], [], [], [], []];

    WEEKDAY_PREFIXES.forEach(({ prefix, jsDay }) => {
      const intervals: WorkInterval[] = [];
      this.addWorkInterval(
        intervals,
        fields[`${prefix}MorningFrom`],
        fields[`${prefix}MorningTo`]
      );
      this.addWorkInterval(
        intervals,
        fields[`${prefix}AfternoonFrom`],
        fields[`${prefix}AfternoonTo`]
      );
      intervalsByWeekday[jsDay] = intervals;
    });

    const validFrom = new Date(workTime.validFrom);
    validFrom.setHours(0, 0, 0, 0);

    let validTo: number | null = null;
    if (workTime.validTo) {
      const validToDate = new Date(workTime.validTo);
      validToDate.setHours(23, 59, 59, 999);
      validTo = validToDate.getTime();
    }

    return { validFrom: validFrom.getTime(), validTo, intervalsByWeekday };
  }

  private addWorkInterval(target: WorkInterval[], from: string, to: string): void {
    const start = this.toMinutesOfDay(from);
    const end = this.toMinutesOfDay(to);
    if (end > start) {
      target.push({ start, end });
    }
  }

  private toMinutesOfDay(time: string): number {
    if (!time) {
      return 0;
    }
    return TimeSpan.parse(time).getTotalSeconds() / 60;
  }
}
